import type { GlideFC } from "@glide/common";
import { ErrorBoundary, GlideIcon, isInIframe } from "@glide/common";
import { PastAndOrFuture } from "@glide/common-core/dist/js/components/past-future";
import { lazilyLoadedComponent } from "@glide/common-core/dist/js/support/react-lazy-loading";

import {
    convertDateToTimeZoneAgnostic,
    dateClearedTime,
    DateFormat,
    dateNoSeconds,
    formatDateTime,
    GlideDateTime,
    TimeFormat,
} from "@glide/data-types";
import type { AppKind } from "@glide/location-common";
import type { DateTimeVariant } from "@glide/wire";
import { definedMap } from "@glideapps/ts-necessities";
import * as React from "react";
import { Suspense } from "react";
import type { FormElementProps } from "../../wire-lib";
import { WireBasePicker } from "../wire-base-picker/wire-base-picker";
import { useIsHoverable } from "@glide/common-components";
import { useFeatureSettings } from "@glide/common-core/dist/js/use-feature-settings";
import { format } from "date-fns";

// @ts-expect-error
interface DateTimePickerInput extends HTMLInputElement {
    showPicker?: () => void;
}

// For mobile, we just keep the current experience for now
const WireMaterialDateTimePicker = lazilyLoadedComponent(
    "material-date-time-picker",
    () => import("./private/wire-material-date-time-picker")
);

// This is the desktop optimized version (new library)
const WireMuiXDateTimePicker = lazilyLoadedComponent(
    "material-date-time-picker",
    () => import("./private/wire-mui-x-date-time-picker")
);

interface Props extends FormElementProps {
    readonly variant: DateTimeVariant;
    readonly placeholder: string;
    readonly value: GlideDateTime | undefined;
    readonly pastAndOrFuture: PastAndOrFuture;
    readonly appKind: AppKind;
    readonly onChange: (value: GlideDateTime | undefined) => void;
}

const formatDateForInput = (date: Date, variant: DateTimeVariant): string => {
    return format(date, variant === "date" ? "yyyy-MM-dd" : "yyyy-MM-dd'T'HH:mm");
};

export const WireDateTimePicker: GlideFC<Props> = p => {
    const [picker, setPicker] = React.useState<Boolean>(false);
    const [pickerValue, setPickerValue] = React.useState<Date | undefined>(undefined);
    const {
        variant,
        title,
        placeholder,
        value: glideDateTime,
        pastAndOrFuture,
        appKind,
        onChange,
        isRequired,
        isEnabled,
    } = p;
    const [useNativeMobileDatePicker, useDesktopDatePicker] = useFeatureSettings([
        "useNativeMobileDatePicker",
        "useDesktopDatePicker",
    ]);
    const isTouch = !useIsHoverable();
    const inIframe = isInIframe();
    // Samsung phones matches against the media query we use for changing hover capabilities
    // so we need to also consider the screenSize in this case
    const nativePickerSupported = !inIframe; // Most browsers don't support native date pickers in iframes
    const showNativePicker = isTouch && useNativeMobileDatePicker && nativePickerSupported;

    const DatePickerComponent = isTouch || !useDesktopDatePicker ? WireMaterialDateTimePicker : WireMuiXDateTimePicker;
    const datePicker = React.useRef<DateTimePickerInput>(null);

    const value = React.useMemo(() => {
        if (glideDateTime === undefined) return undefined;
        return glideDateTime.asTimeZoneAwareDate();
    }, [glideDateTime]);

    const showPicker = React.useCallback((): void => {
        if (showNativePicker && datePicker.current && datePicker.current.showPicker) {
            datePicker.current.showPicker();
            return;
        }
        if (value === undefined) {
            const defaultDateValue = variant === "date" ? dateClearedTime(new Date()) : dateNoSeconds(new Date());
            setPickerValue(defaultDateValue);
        }
        setPicker(true);
    }, [value, variant, showNativePicker]);

    const hidePicker = React.useCallback((): void => {
        setPicker(false);
    }, []);

    const onClear = React.useCallback(
        (ev: React.MouseEvent<HTMLElement>): void => {
            ev.preventDefault();
            ev.stopPropagation();
            onChange?.(undefined);
        },
        [onChange]
    );

    const onUpdate = React.useCallback((dateTime: Date | undefined): void => {
        setPickerValue(dateTime);
    }, []);

    const onCancel = React.useCallback((): void => {
        hidePicker();
    }, [hidePicker]);

    const onAccept = React.useCallback((): void => {
        if (onChange === undefined || pickerValue === null) return;

        onChange(definedMap(pickerValue, v => GlideDateTime.fromTimeZoneAwareDate(v)));

        hidePicker();
    }, [hidePicker, onChange, pickerValue]);

    const disablePast = pastAndOrFuture === PastAndOrFuture.TodayOrFuture;
    const disableFuture = pastAndOrFuture === PastAndOrFuture.TodayOrPast;

    const hasValue = value !== undefined;

    let valueString = placeholder;
    const localDate = definedMap(value, convertDateToTimeZoneAgnostic);
    if (variant === "date" && localDate !== undefined) {
        valueString = formatDateTime(localDate, DateFormat.Short, undefined) ?? "";
    } else if (variant === "date-time" && localDate !== undefined) {
        valueString = formatDateTime(localDate, DateFormat.Short, TimeFormat.WithSeconds) ?? "";
    }

    const onNativeChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>): void => {
            const dateStr = variant === "date" ? `${e.target.value}T00:00:00` : e.target.value;
            let date = new Date(dateStr);

            if (isNaN(date.getTime())) {
                onChange(undefined);
                return;
            }

            if (variant === "date") {
                // This will zero out hours and minutes in the Date
                date = dateClearedTime(date);
            } else if (variant === "date-time") {
                // This will zero out seconds in the Date
                date = dateNoSeconds(date);
            }
            onChange(GlideDateTime.fromTimeZoneAwareDate(date));
        },
        [onChange, variant]
    );

    return (
        <>
            <WireBasePicker
                isEnabled={isEnabled}
                isRequired={isRequired}
                title={title}
                onClick={showPicker}
                leftItem={<GlideIcon kind="monotone" icon="mt-page-calendar" iconSize={20} />}
                rightItem={hasValue ? "clear" : undefined}
                rightItemOnClick={onClear}
                {...p}>
                {showNativePicker ? (
                    <input
                        // @ts-expect-error
                        ref={datePicker}
                        tw="w-full text-left"
                        type={variant === "date" ? "date" : "datetime-local"}
                        value={value === undefined ? "" : formatDateForInput(value, variant)}
                        min={disablePast ? formatDateForInput(new Date(), variant) : undefined}
                        max={disableFuture ? formatDateForInput(new Date(), variant) : undefined}
                        onChange={onNativeChange}
                    />
                ) : (
                    valueString
                )}
            </WireBasePicker>
            {picker && (
                <ErrorBoundary>
                    <Suspense fallback={<></>}>
                        <DatePickerComponent
                            type={variant}
                            value={pickerValue ?? value ?? new Date()}
                            disablePast={disablePast}
                            disableFuture={disableFuture}
                            onUpdate={onUpdate}
                            onCancel={onCancel}
                            onAccept={onAccept}
                            appKind={appKind}
                        />
                    </Suspense>
                </ErrorBoundary>
            )}
        </>
    );
};
